// apps/spa/src/components/commands/CommandStatusBadge.tsx
// Derives the Discord registration status badge from slashCommands D1 fields.
//
// 4-state derivation (CONTEXT decision — locked):
//   Live in Discord  — registeredAt !== null && lastRegisterError === null && enabled === true
//   Registering…     — registeredAt === null && lastRegisterError === null && enabled === true
//   Pending Discord  — lastRegisterError !== null (any value, regardless of registeredAt)
//   Disabled         — enabled === false
//
// The "Registering…" state is transient — the background ctx.waitUntil promise is in flight.
// useCommands() auto-polls every 2s while any row is in this state, so the badge
// transitions to Live or Pending Discord within ~5s of saving.
//
// "Pending Discord" badge is wrapped in a Tooltip to surface lastRegisterError to officers.

import { Badge } from '@/components/ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import type { SlashCommand } from '@guardian/shared';

export interface CommandStatusBadgeProps {
  command: Pick<SlashCommand, 'registeredAt' | 'lastRegisterError' | 'enabled'>;
}

export function CommandStatusBadge({ command }: CommandStatusBadgeProps) {
  // Priority: Disabled > Pending Discord > Registering > Live
  if (!command.enabled) {
    return (
      <Badge variant="secondary" className="text-xs">
        Disabled
      </Badge>
    );
  }

  if (command.lastRegisterError) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger>
            <Badge
              variant="outline"
              className="border-amber-500 text-amber-600 text-xs cursor-default"
            >
              Pending Discord
            </Badge>
          </TooltipTrigger>
          <TooltipContent>
            <span className="max-w-xs truncate">{command.lastRegisterError}</span>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  if (!command.registeredAt) {
    return (
      <Badge
        variant="outline"
        className="border-amber-500 text-amber-600 animate-pulse text-xs"
      >
        Registering…
      </Badge>
    );
  }

  return (
    <Badge className="bg-green-600 text-white hover:bg-green-700 text-xs">
      Live in Discord
    </Badge>
  );
}
